import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, X } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useI18n } from '@/i18n'
import { newId, newUlid } from '@/lib/ids'
import { istDateKey } from '@/lib/ist'
import type { RosterFarmer } from '@/offline'
import {
  Banner,
  Button,
  Card,
  CardBody,
  ChoiceGroup,
  EmptyState,
  Sheet,
  Skeleton,
  Stepper,
  useToast,
} from '@/ui'
import '../../commands'
import { GateScreen } from '../../components/GateScreen'
import { useSupervisorPin } from '../../components/SupervisorPin'
import { commitDelivery, mintLot, saveDeliveryDraft, voidDelivery } from '../../data/actions'
import { useGate } from '../../data/useGate'
import type { GateSession } from '../../data/session'
import type { GateRoster } from '../../data/hooks'
import {
  lotPlan,
  newDraft,
  visibleSteps,
  type DeliveryDraft,
  type DraftDispute,
  type StepId,
} from '../../model/delivery'
import { pickCard } from '../../model/rateCards'
import { usePersistentDraft, WizardContext, type WizardCtx } from './context'
import { FindFarmer } from './FindFarmer'
import { DisputeFlow } from './DisputeFlow'
import { StepDetails } from './StepDetails'
import { useSeasonLight } from './hooks'
import { StepCheck } from './StepCheck'
import { StepMoisture } from './StepMoisture'
import { StepPrice } from './StepPrice'
import { StepTag } from './StepTag'
import { StepSample } from './StepSample'
import { StepSlip } from './StepSlip'
import { CancelFlow, StepClose } from './StepClose'
import type { MessageKey } from '@/i18n'

const STEP_LABEL: Record<StepId, MessageKey> = {
  details: 'gate.step.details',
  check: 'gate.step.check',
  moisture: 'gate.step.moisture',
  price: 'gate.step.price',
  tag: 'gate.step.tag',
  sample: 'gate.step.sample',
  slip: 'gate.step.slip',
  close: 'gate.step.close',
}

export interface PaperSlipHeader {
  /** V1: no receipt book (null). */
  book_id: string | null
  book_no: string | null
  slip_no: string
  slip_time: string
  page_photo_media_id: string | null
}

/**
 * F4.2 → F4.10 — one delivery as a resumable draft. APP mode on the open day; PAPER_BACKFILL
 * mode types in a paper slip (F4-B3.b) through the same screens.
 */
export function DeliveryPage({
  paper = false,
  paperSlip,
  onPaperDone,
}: {
  paper?: boolean
  paperSlip?: PaperSlipHeader
  onPaperDone?: () => void
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const actor = useActor()
  const toast = useToast()
  const g = useGate()
  const [starting, setStarting] = useState(false)
  const [done, setDone] = useState<{ codes: string[]; kind: 'COMMITTED' | 'VOIDED' } | null>(null)
  const session = paper ? g.paper : g.app
  const mode = paper ? 'PAPER_BACKFILL' : 'APP'
  const draftRec = g.drafts.find(
    (x) => x.data.capture_mode === mode && session && x.data.session_id === session.id,
  )
  const staleDraft = g.drafts.find(
    (x) => x.data.capture_mode === mode && (!session || x.data.session_id !== session.id),
  )

  if (!g.ready || !g.roster) return <Skeleton className="h-64 w-full" />

  if (done) {
    return (
      <GateScreen
        title="gate.newDelivery"
        back={paper ? (onPaperDone ?? '/m/gate/paper') : '/m/gate'}
      >
        <EmptyState
          icon={<CheckCircle weight="fill" className="text-ok" />}
          title={t(done.kind === 'COMMITTED' ? 'gate.close.done' : 'gate.cancel.done')}
          description={done.codes.join(' · ')}
          action={
            <div className="flex flex-col gap-2">
              {!paper && (
                <Button size="xl" onClick={() => setDone(null)}>
                  {t('gate.close.nextFarmer')}
                </Button>
              )}
              <Button
                size="touch"
                variant="secondary"
                onClick={() =>
                  paper && onPaperDone
                    ? onPaperDone()
                    : navigate(paper ? '/m/gate/paper' : '/m/gate')
                }
              >
                {t(paper ? 'gate.paper.backToDay' : 'gate.day.title')}
              </Button>
            </div>
          }
        />
      </GateScreen>
    )
  }

  if (!session) {
    return (
      <GateScreen title="gate.newDelivery">
        <Banner tone="warn">{t('gate.delivery.noDay')}</Banner>
        <Button size="xl" onClick={() => navigate(paper ? '/m/gate/paper' : '/m/gate/open-day')}>
          {t(paper ? 'gate.paper.title' : 'gate.openDay')}
        </Button>
        {staleDraft && (
          <Banner tone="error">
            {t('gate.delivery.staleDraft', { code: staleDraft.data.main_lot.code })}
          </Banner>
        )}
      </GateScreen>
    )
  }

  if (!draftRec) {
    const start = async (f: RosterFarmer, identification: DeliveryDraft['identification']) => {
      setStarting(true)
      try {
        const at = paperSlip?.slip_time ?? new Date().toISOString()
        const lot = await mintLot(at)
        const d = newDraft({
          delivery_ulid: newUlid(),
          session_id: session.id,
          capture_mode: mode,
          now: at,
          farmer: {
            id: f.user_id,
            name: f.name,
            roster_no: f.roster_no,
            father_name: f.father_name ?? null,
            phone_last4: f.phone_last4 ?? null,
            photo_url: f.photo_url ?? null,
            bank_verified: f.bank?.verified ?? false,
            account_last4: f.bank?.account_last4 ?? null,
          },
          identification,
          main_lot: lot,
          paper: paperSlip ? { ...paperSlip, receipt_slip_id: newId() } : undefined,
        })
        if (paperSlip?.page_photo_media_id) d.slip.photo_media_id = paperSlip.page_photo_media_id
        await saveDeliveryDraft(d, actor.user_id)
      } catch (e) {
        toast.error(t('gate.saveFailed'), e instanceof Error ? e.message : String(e))
      } finally {
        setStarting(false)
      }
    }
    return (
      <GateScreen
        title="gate.farmer.title"
        back={paper ? (onPaperDone ?? '/m/gate/paper') : '/m/gate'}
      >
        {paperSlip && (
          <Banner tone="info">
            {t('gate.paper.typingSlip', { slip: paperSlip.slip_no, book: paperSlip.book_no ?? '' })}
          </Banner>
        )}
        <FindFarmer
          farmers={g.roster.farmers}
          villageName={session.village_name}
          onConfirm={(f, id) => void start(f, id)}
          busy={starting}
        />
      </GateScreen>
    )
  }

  return (
    <Wizard
      key={draftRec.data.delivery_ulid}
      initial={draftRec.data}
      session={session}
      roster={g.roster}
      outbox={g.outbox}
      used={g.used}
      paperSessionOpen={!!g.paper}
      onDone={(x) => setDone(x)}
      onExit={() => navigate(paper ? '/m/gate/paper' : '/m/gate')}
    />
  )
}

function Wizard({
  initial,
  session,
  roster,
  outbox,
  used,
  paperSessionOpen,
  onDone,
  onExit,
}: {
  initial: DeliveryDraft
  session: GateSession
  roster: GateRoster
  outbox: WizardCtx['outbox']
  used: WizardCtx['used']
  paperSessionOpen: boolean
  onDone: (x: { codes: string[]; kind: 'COMMITTED' | 'VOIDED' }) => void
  onExit: () => void
}) {
  const { t } = useI18n()
  const toast = useToast()
  const actor = useActor()
  const sup = useSupervisorPin()
  const { draft: d, dispatch, flush } = usePersistentDraft(initial, actor.user_id)
  const [disputePrefill, setDisputePrefill] = useState<Partial<
    Pick<DraftDispute, 'about' | 'container_id' | 'bag_position'>
  > | null>(null)
  const [busy, setBusy] = useState(false)
  const [cancelAsk, setCancelAsk] = useState(false)

  const dateKey = istDateKey(d.paper?.slip_time ?? d.started_at)
  const crop = roster.config.crops.find((c) => c.crop_id === d.crop_id)
  const cards = useMemo<WizardCtx['cards']>(() => {
    const out: WizardCtx['cards'] = { MAIN: null, CONV: null, problem: null }
    if (!d.crop_id) return out
    for (const l of lotPlan(d)) {
      const pick = pickCard(roster.rateCards, d.crop_id, l.stream, dateKey)
      if (pick.ok) out[l.stream] = pick.card
      else if (!out.problem) out.problem = { stream: l.stream, kind: pick.problem }
    }
    // Show both prices when he may still have red-tie bags.
    for (const s of ['MAIN', 'CONV'] as const) {
      if (!out[s]) {
        const p = pickCard(roster.rateCards, d.crop_id, s, dateKey)
        if (p.ok) out[s] = p.card
      }
    }
    return out
  }, [d, roster.rateCards, dateKey])

  const ctx: WizardCtx = {
    d,
    dispatch,
    session,
    roster,
    outbox,
    used,
    actor,
    crop,
    cards,
    dateKey,
    stamp: () => d.paper?.slip_time ?? new Date().toISOString(),
    requestSupervisor: sup.request,
    startDispute: (p) => setDisputePrefill(p),
  }
  const light = useSeasonLight(ctx)

  const steps = visibleSteps(d)
  const index = Math.max(0, steps.indexOf(d.step))

  const commit = async () => {
    setBusy(true)
    try {
      await flush()
      const ref = (s: 'MAIN' | 'CONV') => {
        const c = cards[s]
        return c && c.price_paise_per_kg != null
          ? { id: c.id, price_paise_per_kg: c.price_paise_per_kg }
          : undefined
      }
      await commitDelivery(d, session, actor.user_id, {
        cards: { MAIN: ref('MAIN'), CONV: ref('CONV') },
        season_light: light ?? 'AMBER',
      })
      onDone({ codes: lotPlan(d).map((l) => l.code), kind: 'COMMITTED' })
    } catch (e) {
      toast.error(t('gate.saveFailed'), e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const sendVoid = async () => {
    setBusy(true)
    try {
      await flush()
      await voidDelivery(d, session, actor.user_id)
      onDone({ codes: [d.main_lot.code], kind: 'VOIDED' })
    } catch (e) {
      toast.error(t('gate.saveFailed'), e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const divert = async (reasonCode: string) => {
    dispatch({
      type: 'start_cancel',
      reason: 'DIVERTED_TO_DRY',
      reason_code: reasonCode,
      at: ctx.stamp(),
    })
  }

  const body = d.cancel ? (
    <CancelFlow onSend={() => void sendVoid()} sending={busy} />
  ) : (
    {
      details: <StepDetails />,
      check: <StepCheck />,
      moisture: <StepMoisture />,
      price: <StepPrice onDivert={divert} />,
      tag: <StepTag />,
      sample: <StepSample />,
      slip: <StepSlip />,
      close: <StepClose onClose={() => void commit()} closing={busy} />,
    }[d.step]
  )

  return (
    <WizardContext.Provider value={ctx}>
      <GateScreen
        title={STEP_LABEL[d.step]}
        eyebrow={
          d.capture_mode === 'PAPER_BACKFILL'
            ? t('gate.paper.slipEyebrow', { slip: d.paper?.slip_no ?? '' })
            : d.main_lot.code
        }
        back={
          index > 0 && !d.cancel
            ? () => dispatch({ type: 'goto', step: steps[index - 1]! })
            : onExit
        }
        aside={
          !d.cancel && (
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={<X aria-hidden="true" />}
              onClick={() => setCancelAsk(true)}
            >
              {t('gate.cancel.button')}
            </Button>
          )
        }
      >
        <div className="flex items-center gap-2 text-[14px]">
          <span className="font-semibold">{d.farmer.name}</span>
          <span className="font-mono text-muted">#{d.farmer.roster_no}</span>
        </div>
        {!d.cancel && (
          <Stepper steps={steps.map((s) => ({ id: s, label: t(STEP_LABEL[s]) }))} current={index} />
        )}
        {body}
      </GateScreen>
      <DisputeFlow prefill={disputePrefill} onCancelForm={() => setDisputePrefill(null)} />
      {cancelAsk && (
        <CancelAskSheet
          paperAllowed={d.capture_mode === 'APP' && paperSessionOpen}
          onClose={() => setCancelAsk(false)}
          onPick={(reason) => {
            dispatch({ type: 'start_cancel', reason, reason_code: null, at: ctx.stamp() })
            setCancelAsk(false)
          }}
        />
      )}
      {sup.sheet}
    </WizardContext.Provider>
  )
}

function CancelAskSheet({
  paperAllowed,
  onClose,
  onPick,
}: {
  paperAllowed: boolean
  onClose: () => void
  onPick: (r: 'WALK_AWAY' | 'MOVED_TO_PAPER') => void
}) {
  const { t } = useI18n()
  const [reason, setReason] = useState<'WALK_AWAY' | 'MOVED_TO_PAPER'>('WALK_AWAY')
  return (
    <Sheet
      open
      onClose={onClose}
      title={t('gate.cancel.title')}
      footer={
        <Button size="touch" variant="danger" fullWidth onClick={() => onPick(reason)}>
          {t('gate.cancel.button')}
        </Button>
      }
    >
      <Card>
        <CardBody className="flex flex-col gap-3">
          <p className="text-[14px]">{t('gate.cancel.explain')}</p>
          <ChoiceGroup
            layout="stack"
            options={[
              { value: 'WALK_AWAY', label: t('gate.cancel.walkAway') },
              ...(paperAllowed
                ? [{ value: 'MOVED_TO_PAPER' as const, label: t('gate.cancel.movedToPaper') }]
                : []),
            ]}
            value={reason}
            onChange={setReason}
          />
        </CardBody>
      </Card>
    </Sheet>
  )
}
